import { useEffect, useMemo, useState } from 'react'
import { CalendarClock, CalendarPlus, Lightbulb, Tag, TrendingUp } from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import type { ISODate, RatePeriod } from '../data/model'
import { addDays, addMonths, nights, todayISO } from '../lib/dates'
import { HOLIDAY_ATTRIBUTION } from '../lib/holidays'
import {
  ALL_DAYS_MASK, HORIZON_MONTHS, WEEKEND_DOW_MASK, WEEKEND_LOOKBACK_MONTHS,
  buildPriceAdvice, proposedPriority,
  type AdviceSpan, type WeekendAdvice,
} from '../lib/priceAdvice'
import { Badge, Button, Card, Field, Input, Sheet } from '../ui/kit'
import { useTenantHolidays } from './HolidayBands'
import { BookingsPricePlanBlock } from './PricePlanCard'

/**
 * Preisempfehlungen unter den Saisonpreisen.
 *
 * WARUM hier und nicht auf „Heute": es ist keine Aufgabe für den Vormittag, sondern
 * eine Entscheidung, die man trifft, während man ohnehin die Preise ansieht. Direkt
 * über dieser Karte steht die Liste, in die der Knopf schreibt.
 *
 * WAS DIE KARTE VERSPRECHEN DARF: sie rechnet Auslastung, Ferienlage und den eigenen
 * Durchschnittspreis gegeneinander — mehr nicht. Kein Marktpreis, keine Prognose,
 * kein Modell. Der Prozentsatz ist ein Vorschlag, und das steht auch so da; eine Karte,
 * die „optimaler Preis: 137 €" behauptet, verliert beim ersten Gegenbeispiel das
 * Vertrauen für alles andere im Produkt.
 *
 * WARUM ZEITRÄUME: „KW 30, KW 31, KW 32" sind für den Vermieter kein Befund, sondern
 * eine Rechenaufgabe. Der Befund heißt „die Sommerferien" — und genau so steht er hier.
 *
 * In der Demo liegen keine Feriendaten vor. Die Karte bleibt trotzdem sichtbar und
 * argumentiert dann allein aus der Auslastung — plus einen Satz, der sagt, was fehlt.
 * Eine leere Karte in der Vorführung sieht aus wie eine Funktion, die es nicht gibt.
 */
export function PriceAdviceCard() {
  const { lang, formatMoney, formatDate } = useI18n()
  const store = useStore()
  const de = lang === 'de'

  const today = todayISO()
  const horizonEnd = addMonths(today, HORIZON_MONTHS)
  const { byDay } = useTenantHolidays(today, horizonEnd)
  const [proposal, setProposal] = useState<SeasonProposal | null>(null)

  const { units, bookings, rate_periods } = store.db
  const result = useMemo(
    () => buildPriceAdvice({
      today,
      units,
      bookings,
      ratePeriods: rate_periods,
      holidaysByDay: byDay,
    }),
    [today, units, bookings, rate_periods, byDay],
  )

  const pct = (v: number) => `${Math.round(v * 100)}${de ? ' %' : '%'}`
  const showsRegions = result.spans.some((s) => s.holidayRegions.length > 0)

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="font-medium flex-1 flex items-center gap-2">
          <Lightbulb size={18} />
          {de ? 'Preisempfehlungen' : 'Pricing suggestions'}
        </h2>
        {result.averagePrice > 0 && (
          <span className="text-xs text-muted whitespace-nowrap">
            {de ? 'Ø ' : 'avg '}{formatMoney(Math.round(result.averagePrice))}
          </span>
        )}
      </div>

      <p className="text-xs text-muted">
        {de
          ? `Aus Ihren eigenen Zahlen der nächsten ${HORIZON_MONTHS} Monate: Auslastung, Ferien Ihrer Gästemärkte und Ihr aktueller Saisonpreis. Keine Marktdaten, keine Prognose.`
          : `From your own figures for the next ${HORIZON_MONTHS} months: occupancy, school holidays in your guest markets and your current season price. No market data, no forecast.`}
      </p>

      {result.activeUnits === 0 ? (
        <p className="text-sm text-muted">
          {de
            ? 'Sobald eine Einheit aktiv ist, wird hier gerechnet.'
            : 'Once a unit is active there is something to calculate here.'}
        </p>
      ) : result.spans.length === 0 && !result.weekend ? (
        <p className="text-sm text-muted">
          {de
            ? 'Nichts Auffälliges: kein Zeitraum ist ungewöhnlich voll oder ungewöhnlich leer, und für jeden greift eine Saison.'
            : 'Nothing stands out: no period is unusually full or unusually empty, and a season covers every one of them.'}
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {result.spans.map((s) => (
            <li key={s.from} className="py-2.5 space-y-1.5">
              <div className="flex items-baseline gap-2">
                <span className="text-sm font-medium whitespace-nowrap">
                  {weekLabel(s, de)}
                </span>
                <span className="text-xs text-muted truncate flex-1">
                  {formatDate(s.from, { day: '2-digit', month: '2-digit' })}
                  {' – '}
                  {formatDate(s.lastDay, { day: '2-digit', month: '2-digit' })}
                </span>
                <SuggestionBadge span={s} de={de} />
              </div>

              <div className="flex items-center gap-3 text-xs text-muted">
                <span className="tabular-nums">
                  {de ? 'Auslastung' : 'Occupancy'} {pct(s.occupancy)}
                </span>
                <span className="tabular-nums">
                  {de ? 'Preis' : 'Price'} {formatMoney(Math.round(s.averagePrice))}
                  {de ? '/Nacht' : '/night'}
                </span>
              </div>

              <p className="text-sm">{reasonText(s, de, result.noSeasonWeeks)}</p>

              {s.suggestedPrice != null && (
                <Button size="sm" variant="secondary"
                  onClick={() => setProposal(spanProposal(s, de))}>
                  <CalendarPlus size={15} />
                  {de
                    ? `Als Saison anlegen — ${formatMoney(s.suggestedPrice)}/Nacht`
                    : `Create as a season — ${formatMoney(s.suggestedPrice)}/night`}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {result.weekend && (
        <WeekendBlock
          weekend={result.weekend}
          onApply={() => setProposal(weekendProposal(result.weekend!, de))}
        />
      )}

      {/* Die KI setzt fort, was hier gerechnet wurde: sie ordnet die Zahlen ein und
          formuliert die Begründung. Deshalb unter den Befunden und nicht daneben —
          erst die Rechnung, dann das Urteil. */}
      {result.activeUnits > 0 && <BookingsPricePlanBlock result={result} />}

      {result.activeUnits > 0 && !result.hasHolidayData && (
        <p className="text-xs text-muted">
          {de
            ? 'Für Ihre Gästemärkte liegen noch keine Ferientermine vor — die Vorschläge stützen sich hier allein auf Ihre Auslastung. Gästemärkte hinterlegen Sie in den Einstellungen, dann wird die Ferienlage mitgerechnet.'
            : 'No school-holiday dates are available for your guest markets yet, so these suggestions rest on your occupancy alone. Add your guest markets in the settings and the holiday overlap is taken into account.'}
        </p>
      )}

      <p className="text-xs text-muted border-t border-border pt-3">
        {de
          ? 'Die Prozentsätze sind ein Vorschlag aus Ihren eigenen Zahlen, keine Berechnung eines Marktpreises — prüfen Sie sie gegen das, was Sie über Ihren Ort wissen. Was Sie hier anlegen, landet als ganz normale Saison in der Liste oben und lässt sich dort jederzeit ändern oder löschen.'
          : 'The percentages are a suggestion drawn from your own figures, not a calculated market price — check them against what you know about your area. Anything you create here becomes an ordinary season in the list above and can be changed or deleted there at any time.'}
      </p>

      {/* ODbL: die Lizenz verlangt den Hinweis überall dort, wo die Feriendaten
          sichtbar werden — hier sind das die Regionsnamen im Begründungssatz. */}
      {showsRegions && (
        <p className="text-[10px] text-muted opacity-70">{HOLIDAY_ATTRIBUTION}</p>
      )}

      <SeasonProposalSheet proposal={proposal} onClose={() => setProposal(null)} />
    </Card>
  )
}

/* ------------------------------------------------------- Wochenendzuschlag */

const DOW_SHORT = {
  de: ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'],
  en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
}

/**
 * Der Wochenendzuschlag mit seiner Begründung.
 *
 * Die sieben Balken stehen dort, weil der Satz allein („78 % gegen 41 %") wie eine
 * Behauptung wirkt. Sieht der Vermieter die Verteilung, erkennt er sein eigenes Haus
 * wieder — oder er erkennt es nicht, und dann soll er dem Vorschlag misstrauen.
 */
function WeekendBlock({ weekend, onApply }: { weekend: WeekendAdvice; onApply: () => void }) {
  const { lang, formatMoney } = useI18n()
  const de = lang === 'de'
  const days = DOW_SHORT[de ? 'de' : 'en']
  const pct = (v: number) => `${Math.round(v * 100)}${de ? ' %' : '%'}`

  return (
    <div className="border-t border-border pt-3 space-y-2">
      <div className="flex items-baseline gap-2">
        <h3 className="text-sm font-medium flex-1">
          {de ? 'Wochenendzuschlag' : 'Weekend surcharge'}
        </h3>
        <Badge tone="accent" className="whitespace-nowrap">
          <TrendingUp size={12} />
          +{weekend.suggestedPct} %
        </Badge>
      </div>

      <div className="flex gap-1">
        {weekend.byWeekday.map((d) => {
          const weekendDay = (WEEKEND_DOW_MASK & (1 << d.dow)) !== 0
          return (
            <div key={d.dow} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full h-10 bg-surface-2 rounded flex items-end overflow-hidden">
                <div
                  className={weekendDay ? 'w-full bg-accent' : 'w-full bg-border'}
                  style={{ height: `${Math.round(d.occupancy * 100)}%` }}
                />
              </div>
              <span className="text-[10px] text-muted">{days[d.dow]}</span>
            </div>
          )
        })}
      </div>

      <p className="text-sm">
        {de
          ? `Freitag- und Samstagnächte waren in den letzten ${WEEKEND_LOOKBACK_MONTHS} Monaten zu ${pct(weekend.weekendOccupancy)} belegt, Montag bis Donnerstag nur zu ${pct(weekend.weekdayOccupancy)}. Einen Zuschlag für diese beiden Nächte haben Sie bisher nicht hinterlegt.`
          : `Friday and Saturday nights ran at ${pct(weekend.weekendOccupancy)} over the past ${WEEKEND_LOOKBACK_MONTHS} months, Monday to Thursday only at ${pct(weekend.weekdayOccupancy)}. You have no surcharge for those two nights yet.`}
      </p>

      <Button size="sm" variant="secondary" onClick={onApply}>
        <CalendarPlus size={15} />
        {de
          ? `Zuschlag anlegen — ${formatMoney(weekend.suggestedPrice)}/Nacht an Fr und Sa`
          : `Add the surcharge — ${formatMoney(weekend.suggestedPrice)}/night on Fri and Sat`}
      </Button>
    </div>
  )
}

/* ------------------------------------------------------ Anlegen per Knopf */

/**
 * Was der Knopf anlegen würde — in der Sprache des Vermieters, nicht in der des
 * Schemas. Beide Vorschlagsarten (Saison, Zuschlag) enden hier, weil beide dieselbe
 * Zeile in `rate_periods` schreiben und beide dieselbe Frage beantworten müssen:
 * welcher Zeitraum, welcher Preis, welcher Bereich.
 */
export interface SeasonProposal {
  title: string
  /** Vorschlag für die Bezeichnung; im Sheet änderbar. */
  name: string
  from: ISODate
  /** Einschließlich — so, wie `valid_to` gelesen wird. */
  lastDay: ISODate
  dowMask: number
  price: number
  /** Was derselbe Zeitraum heute im Schnitt kostet. */
  currentPrice: number
  /** Ein Satz, worauf der Vorschlag beruht. */
  because: string
}

/** „KW 30–32" bzw. „KW 32" — die Sprache, in der über Saisons geredet wird. */
function weekLabel(span: AdviceSpan, de: boolean): string {
  const first = span.weeks[0]!.isoWeek
  const last = span.weeks[span.weeks.length - 1]!.isoWeek
  const prefix = de ? 'KW' : 'Week'
  return first === last ? `${prefix} ${first}` : `${prefix} ${first}–${last}`
}

export function spanProposal(span: AdviceSpan, de: boolean): SeasonProposal | null {
  if (span.suggestedPrice == null) return null
  const occ = `${Math.round(span.occupancy * 100)}${de ? ' %' : '%'}`
  return {
    title: de ? 'Saison anlegen' : 'Create a season',
    name: de ? `Saison ${weekLabel(span, true)}` : `Season ${weekLabel(span, false)}`,
    from: span.from,
    lastDay: span.lastDay,
    dowMask: ALL_DAYS_MASK,
    price: span.suggestedPrice,
    currentPrice: Math.round(span.averagePrice),
    because: de
      ? `${occ} Auslastung in diesem Zeitraum${span.holidayRegions.length > 0 ? `, Ferien in ${span.holidayRegions.join(', ')}` : ''}.`
      : `${occ} occupancy in this period${span.holidayRegions.length > 0 ? `, school holidays in ${span.holidayRegions.join(', ')}` : ''}.`,
  }
}

export function weekendProposal(w: WeekendAdvice, de: boolean): SeasonProposal {
  const pct = (v: number) => `${Math.round(v * 100)}${de ? ' %' : '%'}`
  return {
    title: de ? 'Wochenendzuschlag anlegen' : 'Add a weekend surcharge',
    name: de ? 'Wochenendzuschlag' : 'Weekend surcharge',
    from: w.validFrom,
    lastDay: w.validTo,
    dowMask: WEEKEND_DOW_MASK,
    price: w.suggestedPrice,
    currentPrice: Math.round(w.averagePrice),
    because: de
      ? `Fr/Sa ${pct(w.weekendOccupancy)} gegen ${pct(w.weekdayOccupancy)} Mo–Do in den letzten ${WEEKEND_LOOKBACK_MONTHS} Monaten.`
      : `Fri/Sat ${pct(w.weekendOccupancy)} against ${pct(w.weekdayOccupancy)} Mon–Thu over the past ${WEEKEND_LOOKBACK_MONTHS} months.`,
  }
}

function maskLabel(mask: number, de: boolean): string {
  const days = DOW_SHORT[de ? 'de' : 'en']
  if (mask === ALL_DAYS_MASK) return de ? 'alle Tage' : 'every day'
  return days.filter((_, i) => (mask & (1 << i)) !== 0).join(', ')
}

/**
 * Die Bestätigung vor dem Anlegen.
 *
 * WARUM KEIN DIREKTER KLICK: der Knopf schreibt in die Preisliste, und wer nicht
 * vorher gesehen hat, was genau dort landet, sucht die Änderung später in der falschen
 * Ecke. Vier Zeilen — Zeitraum, Tage, Bereich, Preis — und der eine Satz, der die
 * häufigste Sorge beantwortet: bestehende Buchungen ändern sich nicht.
 *
 * Name und Preis sind änderbar, weil der Vorschlag ein Vorschlag ist. Wer daraus
 * 125 € statt 129 € macht, soll dafür nicht in ein zweites Formular wechseln müssen.
 */
export function SeasonProposalSheet({
  proposal, onClose,
}: {
  proposal: SeasonProposal | null
  onClose: () => void
}) {
  const { lang, formatMoney, formatDate } = useI18n()
  const store = useStore()
  const de = lang === 'de'
  const [name, setName] = useState('')
  const [price, setPrice] = useState(0)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!proposal) return
    setName(proposal.name)
    setPrice(proposal.price)
  }, [proposal])

  if (!proposal) return null

  const priority = proposedPriority(
    store.db.rate_periods, proposal.from, proposal.lastDay, proposal.dowMask)
  const nightCount = nights(proposal.from, addDays(proposal.lastDay, 1))

  const create = async () => {
    setSaving(true)
    const row: RatePeriod = {
      id: crypto.randomUUID(),
      tenant_id: store.db.tenant.id,
      unit_id: null,
      category_id: null,
      name: name.trim() || proposal.name,
      valid_from: proposal.from,
      valid_to: proposal.lastDay,
      dow_mask: proposal.dowMask,
      price_per_night: price,
      extra_person_price: null,
      base_occupancy: null,
      min_nights: null,
      priority,
      active: true,
      online_enquiry: true,
    }
    await store.upsertRatePeriod(row)
    setSaving(false)
    onClose()
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title={proposal.title}
      footer={
        <>
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            {de ? 'Abbrechen' : 'Cancel'}
          </Button>
          <Button variant="primary" className="flex-1" disabled={saving || price <= 0}
            onClick={() => void create()}>
            {de ? 'Anlegen' : 'Create'}
          </Button>
        </>
      }
    >
      <p className="text-sm text-muted">{proposal.because}</p>

      <dl className="text-sm divide-y divide-border">
        <Row label={de ? 'Zeitraum' : 'Period'}>
          {formatDate(proposal.from)} – {formatDate(proposal.lastDay)}
          <span className="text-muted">
            {' · '}
            {nightCount} {de ? 'Nächte' : 'nights'}
          </span>
        </Row>
        <Row label={de ? 'Gilt an' : 'Applies on'}>{maskLabel(proposal.dowMask, de)}</Row>
        <Row label={de ? 'Bereich' : 'Scope'}>
          {de ? 'Alle Einheiten — das ganze Haus' : 'All units — the whole property'}
        </Row>
        <Row label={de ? 'Preis heute' : 'Price today'}>
          <span className="tabular-nums">{formatMoney(proposal.currentPrice)}</span>
          <span className="text-muted">{de ? ' im Schnitt/Nacht' : ' on average/night'}</span>
        </Row>
        <Row label={de ? 'Priorität' : 'Priority'}>
          <span className="tabular-nums">{priority}</span>
          <span className="text-muted">
            {proposal.dowMask === ALL_DAYS_MASK
              ? (de
                  ? ' — gewinnt gegen die Saisonpreise, die diesen Zeitraum heute bepreisen'
                  : ' — wins over the seasons currently pricing this period')
              : (de
                  ? ' — liegt über allen Saisonpreisen, an Fr und Sa gilt dieser hier'
                  : ' — sits above every season; on Fri and Sat this one applies')}
          </span>
        </Row>
      </dl>

      <Field label={de ? 'Bezeichnung' : 'Name'}>
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field
        label={de ? 'Preis pro Nacht' : 'Price per night'}
        hint={de ? 'Vorschlag — änderbar' : 'A suggestion — change it freely'}
      >
        <Input type="number" step="0.01" min={0} value={price}
          onChange={(e) => setPrice(Number(e.target.value))} />
      </Field>

      <p className="text-sm rounded-xl bg-surface-2 p-3">
        {de
          ? 'Bestehende Buchungen in diesem Zeitraum ändern sich dadurch NICHT — sie sind bereits bepreist und behalten ihren Betrag. Der neue Preis gilt für jede Buchung und jedes Angebot, das ab jetzt entsteht.'
          : 'Existing bookings in this period are NOT changed — they are already priced and keep their amounts. The new price applies to every booking and quote created from now on.'}
      </p>
      <p className="text-xs text-muted">
        {de
          ? 'Der Prozentsatz ist ein Vorschlag aus Ihren eigenen Zahlen — Auslastung, Ferienlage, Ihr bisheriger Preis. Keine Marktpreisberechnung.'
          : 'The percentage is a suggestion drawn from your own figures — occupancy, school holidays, your current price. Not a market price calculation.'}
      </p>
    </Sheet>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3 py-2">
      <dt className="text-muted w-28 shrink-0">{label}</dt>
      <dd className="flex-1 min-w-0">{children}</dd>
    </div>
  )
}

/* ------------------------------------------------------------- Begründungen */

function SuggestionBadge({ span, de }: { span: AdviceSpan; de: boolean }) {
  if (span.suggestedPct != null) {
    return (
      <Badge tone="accent" className="whitespace-nowrap">
        <TrendingUp size={12} />
        +{span.suggestedPct} %
      </Badge>
    )
  }
  if (span.kind === 'gap') {
    return (
      <Badge tone="warn" className="whitespace-nowrap">
        <CalendarClock size={12} />
        {de ? 'prüfen' : 'check'}
      </Badge>
    )
  }
  return (
    <Badge tone="info" className="whitespace-nowrap">
      <Tag size={12} />
      {de ? 'ohne Saison' : 'no season'}
    </Badge>
  )
}

/** „Bayern, Tirol und Nordrhein-Westfalen" — Aufzählung, wie man sie spricht. */
function joinRegions(regions: string[], de: boolean): string {
  if (regions.length <= 1) return regions[0] ?? ''
  const last = regions[regions.length - 1]!
  return `${regions.slice(0, -1).join(', ')} ${de ? 'und' : 'and'} ${last}`
}

/**
 * Die Begründung in einem Satz.
 *
 * Ein Satz, weil eine Empfehlung ohne nachvollziehbaren Grund entweder blind befolgt
 * oder blind ignoriert wird — und beides ist schlechter als gar keine Empfehlung.
 */
function reasonText(s: AdviceSpan, de: boolean, noSeasonWeeks: number): string {
  const occ = `${Math.round(s.occupancy * 100)}${de ? ' %' : '%'}`

  if (s.kind === 'no_season') {
    const base = de
      ? `Für diesen Zeitraum ist keine Saison hinterlegt, es greift der Grundpreis der Einheit — die häufigste Ursache für zu niedrige Preise.`
      : `No season covers this period, so the unit’s base price applies — the most common reason for prices that are too low.`
    const full = s.suggestedPct != null
      ? (de
          ? ` Dabei sind Sie hier zu ${occ} belegt.`
          : ` And you are ${occ} booked here.`)
      : ''
    const more = noSeasonWeeks > s.weeks.length
      ? (de
          ? ` Betrifft ${noSeasonWeeks} Wochen im Zeitraum.`
          : ` ${noSeasonWeeks} weeks in this window are affected.`)
      : ''
    return base + full + more
  }

  if (s.kind === 'raise') {
    if (s.holidayRegions.length > 0) {
      const list = joinRegions(s.holidayRegions, de)
      const many = s.holidayRegions.length > 1
      return de
        ? `${list} ${many ? 'haben' : 'hat'} Ferien und Sie sind zu ${occ} belegt — Ihr Preis liegt dabei nicht über Ihrem eigenen Schnitt.`
        : `${list} ${many ? 'are' : 'is'} on school holidays and you are ${occ} booked — and priced no higher than your own average.`
    }
    return de
      ? `Sie sind zu ${occ} belegt und liegen preislich nicht über Ihrem Schnitt der nächsten Monate.`
      : `You are ${occ} booked and priced no higher than your own average for the coming months.`
  }

  const weeks = Math.round(s.leadDays / 7)
  const when = de
    ? weeks <= 1 ? 'nächste Woche' : `in ${weeks} Wochen`
    : weeks <= 1 ? 'next week' : `in ${weeks} weeks`
  return de
    ? `Lücke ${when}, ${occ} belegt — Mindestaufenthalt senken oder Preis prüfen.`
    : `Gap ${when}, ${occ} booked — lower the minimum stay or check the price.`
}
